import axios from 'axios';
import CryptoJS from 'crypto-js';

class XrplToService {
  constructor() {
    this.apiBaseUrl = 'https://api.xrpl.to/api';
    this.cachedData = new Map();
    this.cacheExpiry = new Map();
    this.pendingRequests = new Map(); // Track in-flight requests
    
    // Configure axios defaults for better CORS handling
    axios.defaults.headers.common['Accept'] = 'application/json';
    axios.defaults.headers.common['Content-Type'] = 'application/json';
  }

  /**
   * Get tokens from XRPL.to API with sorting and pagination
   * @param {number} start - start value for pagination
   * @param {number} limit - limit count value for pagination (max 100)
   * @param {string} sortBy - can be one of: name, exch, pro24h, pro7d, vol24hxrp, vol24htx, marketcap, trustlines, supply, trendingScore, createdAt
   * @param {string} sortType - can be 'asc' or 'desc'
   * @param {string} filter - filter string (optional)
   * @param {boolean} showNew - whether to show new tokens (optional)
   * @returns {Promise<Object>} - API response with tokens array and metadata
   */
  async getTokens(start = 0, limit = 20, sortBy = 'trendingScore', sortType = 'desc', filter = '', showNew = false) {
    // Validate parameters
    if (limit > 100) limit = 100;
    
    // Ensure sortBy is one of the valid options
    const validSortOptions = [
      'name', 'exch', 'pro24h', 'pro7d', 'vol24hxrp', 'vol24htx', 
      'marketcap', 'trustlines', 'supply', 'trendingScore', 'createdAt'
    ];
    
    if (!validSortOptions.includes(sortBy)) {
      sortBy = 'trendingScore';
    }
    
    // Ensure sortType is valid
    sortType = sortType === 'asc' ? 'asc' : 'desc';
    
    const cacheKey = `tokens-${start}-${limit}-${sortBy}-${sortType}-${filter}-${showNew}`;
    return this.getCachedOrFetch(cacheKey, () => 
      this._fetchTokens(start, limit, sortBy, sortType, filter, showNew)
    );
  }

  async _fetchTokens(start, limit, sortBy, sortType, filter, showNew) {
    try {
      const url = `${this.apiBaseUrl}/tokens`;
      
      // Set shorter cache duration for market data
      const cacheDuration = 60 * 1000; // 1 minute
      
      // For creation date sorting, ensure newest tokens appear first
      let apiSortBy = sortBy;
      let apiSortType = sortType;
      
      if (sortBy === 'createdAt') {
        // Use dateon as the API parameter (matching xrpl.to's API)
        apiSortBy = 'dateon';
        
        // Force DESC when sorting by creation date to show newest first
        apiSortType = 'desc';
        
        console.log('Creation date sorting enabled: newest tokens will appear first');
      } else if (showNew === true) {
        // When showNew is true, always sort by creation date (newest first)
        apiSortBy = 'dateon';
        apiSortType = 'desc';
      }
      
      // Convert boolean to string format expected by API
      const showNewValue = showNew === true ? 'true' : 'false';
      
      // Always use a high limit for getting new tokens
      const actualLimit = 100;
      
      const params = {
        start,
        limit: actualLimit,
        sortBy: apiSortBy,
        sortType: apiSortType,
        filter: filter ? filter.trim() : '',
        showNew: showNewValue,
        showSlug: true,
        tags: ''
      };
      
      console.log('Fetching tokens with params:', params);
      
      // Use a timeout to prevent hanging requests
      const response = await axios.get(url, { 
        params,
        timeout: 10000, // 10 second timeout
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      // Process response to ensure proper number formatting for market caps
      if (response.data && response.data.tokens) {
        // Ensure all market cap values are properly formatted numbers
        response.data.tokens = response.data.tokens.map(token => {
          if (token.marketcap) {
            // Parse and format to ensure it's a valid number
            const marketcap = parseFloat(token.marketcap);
            if (!isNaN(marketcap)) {
              token.marketcap = marketcap.toString();
            }
          }
          return token;
        });
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching tokens from XRPL.to:', error);
      // Return empty data structure to prevent UI errors
      return { tokens: [], result: 'error' };
    }
  }

  /**
   * Get detailed information about a specific token
   * @param {string} issuer - The issuer address of the token
   * @param {string} currency - The currency code of the token
   * @returns {Promise<Object>} - Token details
   */
  async getTokenInfo(issuer, currency) {
    const cacheKey = `token-${issuer}-${currency}`;
    return this.getCachedOrFetch(cacheKey, () => 
      this._fetchTokenInfo(issuer, currency)
    );
  }

  async _fetchTokenInfo(issuer, currency) {
    try {
      const response = await axios.get(`${this.apiBaseUrl}/token/${issuer}_${currency}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching token info:', error);
      throw error;
    }
  }

  async getTokenSparkline(md5) {
    const cacheKey = `sparkline-${md5}`;
    return this.getCachedOrFetch(cacheKey, () => 
      this._fetchTokenSparkline(md5)
    );
  }

  async _fetchTokenSparkline(md5) {
    try {
      const response = await axios.get(`${this.apiBaseUrl}/sparkline/${md5}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching token sparkline:', error);
      return null;
    }
  }

  async getRichList(md5, start = 0, limit = 20) {
    const cacheKey = `richlist-${md5}-${start}-${limit}`;
    return this.getCachedOrFetch(cacheKey, () => 
      this._fetchRichList(md5, start, limit)
    );
  }

  async _fetchRichList(md5, start, limit) {
    try {
      const response = await axios.get(`${this.apiBaseUrl}/richlist/${md5}?start=${start}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching rich list:', error);
      return null;
    }
  }

  async getExchangeHistory(md5, page = 0, limit = 10) {
    const cacheKey = `history-${md5}-${page}-${limit}`;
    return this.getCachedOrFetch(cacheKey, () => 
      this._fetchExchangeHistory(md5, page, limit)
    );
  }

  async _fetchExchangeHistory(md5, page, limit) {
    try {
      const response = await axios.get(`${this.apiBaseUrl}/history?md5=${md5}&page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching exchange history:', error);
      return null;
    }
  }

  async getStatus() {
    const cacheKey = `status`;
    return this.getCachedOrFetch(cacheKey, () => 
      this._fetchStatus()
    );
  }

  async _fetchStatus() {
    try {
      const response = await axios.get(`${this.apiBaseUrl}/status`);
      return response.data;
    } catch (error) {
      console.error('Error fetching status:', error);
      return null;
    }
  }

  // Generate MD5 hash for token identification
  generateMd5(issuer, currency) {
    return CryptoJS.MD5(issuer + '_' + currency).toString();
  }

  // Improved utility method to get cached data or fetch new data
  // Now also handles concurrent requests for the same resource
  async getCachedOrFetch(key, fetchFn, cacheDuration = 300000) { // Default to 5 minutes
    const now = Date.now();
    
    // Return cached data if available and not expired
    if (this.cachedData.has(key) && this.cacheExpiry.get(key) > now) {
      return this.cachedData.get(key);
    }
    
    // If a request for this key is already in progress, wait for it
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }
    
    // Create a new request and store the promise
    const requestPromise = (async () => {
      try {
        const data = await fetchFn();
        this.cachedData.set(key, data);
        this.cacheExpiry.set(key, now + cacheDuration);
        return data;
      } finally {
        // Remove from pending requests when done
        this.pendingRequests.delete(key);
      }
    })();
    
    this.pendingRequests.set(key, requestPromise);
    return requestPromise;
  }
  
  // Clear cache for specific key or all cache if no key provided
  clearCache(key = null) {
    if (key) {
      this.cachedData.delete(key);
      this.cacheExpiry.delete(key);
    } else {
      this.cachedData.clear();
      this.cacheExpiry.clear();
    }
  }

  /**
   * Get a token by its issuer address and currency code
   * This method will work even when the main token list filtering doesn't
   * @param {string} issuer - The issuer address
   * @param {string} currency - The currency code
   * @returns {Promise<Object>} - Token data if found
   */
  async getTokenByIssuerAndCurrency(issuer, currency) {
    try {
      if (!issuer || !currency) {
        throw new Error('Issuer and currency are required');
      }
      
      // First try to get via direct endpoint
      try {
        const response = await axios.get(`${this.apiBaseUrl}/token/${issuer}_${currency}`);
        if (response.data && response.data.token) {
          // Return in the same format as getTokens would
          return {
            result: 'success',
            tokens: [response.data.token]
          };
        }
      } catch (error) {
        console.log('Direct token lookup failed, trying alternative method:', error.message);
      }
      
      // Fallback to using the tokens endpoint with a very specific filter
      // Try with issuer_currencies endpoint first (most reliable for issuer search)
      try {
        const issuerResponse = await axios.get(`${this.apiBaseUrl}/issuer_currencies`, {
          params: {
            issuer: issuer
          }
        });
        
        if (issuerResponse.data && Array.isArray(issuerResponse.data.currencies)) {
          // If we find currencies, map them to token format
          if (issuerResponse.data.currencies.length > 0) {
            const tokens = issuerResponse.data.currencies.map(curr => ({
              issuer: issuer,
              currency: curr.currency,
              name: curr.currency_name || curr.currency,
              supply: curr.amount || "0"
            }));
            
            // Find exact match if currency is specified
            if (currency) {
              const exactMatch = tokens.find(token => 
                token.currency.toLowerCase() === currency.toLowerCase());
              
              if (exactMatch) {
                return {
                  result: 'success',
                  tokens: [exactMatch]
                };
              }
            }
            
            // Return all tokens from issuer if no exact match or no currency specified
            return {
              result: 'success',
              tokens: tokens
            };
          }
        }
      } catch (error) {
        console.log('Issuer currencies lookup failed, trying broader search:', error.message);
      }
      
      // Last resort - try with general token search
      const md5 = this.generateMd5(issuer, currency);
      const response = await axios.get(`${this.apiBaseUrl}/tokens`, {
        params: {
          filter: `${issuer}`,
          limit: 100,
          sortBy: 'name',
          sortType: 'asc',
          start: 0
        }
      });
      
      if (response.data && response.data.tokens) {
        // Find the exact match in results
        const exactMatch = response.data.tokens.find(
          token => token.issuer === issuer && token.currency === currency
        );
        
        if (exactMatch) {
          return {
            result: 'success',
            tokens: [exactMatch]
          };
        }
        
        // If no exact match, return all tokens from that issuer
        const issuerTokens = response.data.tokens.filter(token => token.issuer === issuer);
        if (issuerTokens.length > 0) {
          return {
            result: 'success', 
            tokens: issuerTokens
          };
        }
      }
      
      return { result: 'error', tokens: [] };
    } catch (error) {
      console.error('Error in getTokenByIssuerAndCurrency:', error);
      return { result: 'error', tokens: [] };
    }
  }

  /**
   * Get detailed token information using the xrpl.to API
   * @param {string} issuer - The issuer address of the token
   * @param {string} currency - The currency code of the token
   * @returns {Promise<Object>} - Complete token information with market data
   */
  async getTokenInfoByKey(issuer, currency) {
    try {
      const cacheKey = `token-info-${issuer}-${currency}`;
      return this.getCachedOrFetch(cacheKey, async () => {
        // Use the direct API endpoint as requested
        const response = await axios.get(`https://api.xrpl.to/api/token/${issuer}_${currency}`);
        return response.data;
      }, 30000); // 30 seconds cache
    } catch (error) {
      console.error('Error fetching token info by key:', error);
      return { res: "error", token: null };
    }
  }

  /**
   * Search for tokens using the POST-based search API
   * @param {string} searchQuery - The search query
   * @returns {Promise<Object>} - Search results
   */
  async searchTokens(searchQuery) {
    const cacheKey = `search-${searchQuery}`;
    return this.getCachedOrFetch(cacheKey, () => 
      this._performSearch(searchQuery)
    );
  }

  async _performSearch(searchQuery) {
    try {
      console.log('Performing search with query:', searchQuery);
      
      // Use the POST-based search API as shown in the example
      const response = await axios.post(`${this.apiBaseUrl}/search`, {
        search: searchQuery
      }, {
        timeout: 15000, // 15 second timeout (search can take longer)
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      console.log(`Search completed in ${response.data.took}ms`);
      return response.data;
    } catch (error) {
      console.error('Error performing token search:', error);
      // Return a valid response structure with empty results to prevent UI errors
      return { 
        result: 'error', 
        tokens: [],
        error: error.message
      };
    }
  }
}

const xrplToService = new XrplToService();
export default xrplToService; 